import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FileX2, ArrowRight, Search } from "lucide-react";

/**
 * Brutalist "file redacted" 404. Mimics a classified document stamp — the URL
 * the user tried becomes the "case file" that's been pulled.
 */
export default function NotFoundPage() {
  const location = useLocation();
  const [stampRotation] = useState(() => -6 + Math.random() * 12);

  // Wipe the title so SEO crawlers don't get a bare "Not Found"
  useEffect(() => {
    document.title = "FILE REDACTED // V. Jones Vault";
  }, []);

  // Generate a fake case-file number from the URL for flavor
  const caseFile = (location.pathname || "/")
    .replace(/[^a-z0-9]/gi, "")
    .toUpperCase()
    .slice(0, 8)
    .padEnd(8, "X");

  return (
    <div data-testid="not-found-page" className="max-w-5xl mx-auto px-4 sm:px-8 py-16 min-h-[70vh]">
      <div className="h-[3px] w-full bg-[#ff3b30] mb-10" />

      <div className="font-mono text-[10px] tracking-[0.3em] text-[#ff3b30] mb-4">
        // CLASSIFICATION: RESTRICTED
      </div>

      <div className="border border-white/10 bg-[#0a0a0a] p-8 sm:p-12 relative overflow-hidden">
        {/* Stamp */}
        <div
          aria-hidden="true"
          className="absolute -top-3 right-4 sm:right-12 pointer-events-none select-none"
          style={{ transform: `rotate(${stampRotation}deg)` }}
        >
          <div className="border-4 border-[#ff3b30] text-[#ff3b30] px-5 py-2 font-heading font-black text-3xl sm:text-5xl tracking-tighter uppercase opacity-80">
            Redacted
          </div>
        </div>

        <div className="flex items-center gap-3 mb-6">
          <FileX2 size={28} className="text-[#ff3b30]" />
          <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500">
            CASE FILE // {caseFile}
          </div>
        </div>

        <h1 className="font-heading font-black uppercase tracking-tighter text-5xl sm:text-7xl text-white leading-[0.9] mb-6">
          File Not <span className="text-[#ff3b30]">Located</span>
        </h1>

        <p className="text-sm sm:text-base text-zinc-300 leading-relaxed max-w-2xl mb-3">
          The page at{" "}
          <code className="px-1.5 py-0.5 bg-[#050505] border border-white/10 text-[#ff3b30] font-mono text-xs">
            {location.pathname}
          </code>{" "}
          has been pulled from circulation, never existed, or is currently
          classified above your clearance level.
        </p>
        <p className="text-sm text-zinc-500 leading-relaxed max-w-2xl">
          The archive remains intact &mdash; head back to the front page or surface
          a random file from the deep end.
        </p>

        {/* Mock redacted lines for texture */}
        <div className="my-8 space-y-1.5 opacity-60" aria-hidden="true">
          {[88, 64, 92, 71, 80].map((w, i) => (
            <div
              key={i}
              className="h-3 bg-zinc-700"
              style={{ width: `${w}%` }}
            />
          ))}
        </div>

        <div className="flex flex-wrap gap-3">
          <Link
            to="/"
            data-testid="not-found-home"
            className="inline-flex items-center gap-2 bg-[#ff3b30] text-[#050505] hover:bg-white px-4 py-3 font-mono text-[11px] tracking-[0.25em] uppercase transition-colors"
          >
            Return to Vault <ArrowRight size={14} />
          </Link>
          <Link
            to="/archive"
            data-testid="not-found-archive"
            className="inline-flex items-center gap-2 bg-transparent text-[#ff3b30] border border-[#ff3b30]/50 hover:bg-[#ff3b30] hover:text-[#050505] px-4 py-3 font-mono text-[11px] tracking-[0.25em] uppercase transition-colors"
          >
            <Search size={13} /> Browse Archive
          </Link>
        </div>
      </div>

      <div className="mt-8 font-mono text-[10px] tracking-[0.3em] text-zinc-600">
        // END OF FILE
      </div>
    </div>
  );
}
